import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, GraduationCap, Plus, Users } from 'lucide-react';
import { Button } from 'components/ui/button';
import EmptyState from 'components/ui/EmptyState';
import * as TeamsModule from 'modules/teams';
import * as CollectionsModule from 'modules/collections';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { GroupProfile, GroupProfileSkeleton } from './GroupProfile';
import { MembersGradebook } from './MembersGradebook';
import { GroupMaterialsTab } from './GroupMaterialsTab';
import { AssignCollectionDialog } from './AssignCollectionDialog';
import { InviteStudentsModal } from './InviteStudentsModal';
import { useIsMobile } from 'hooks/use-media-query';
import { TeamDetailMobile } from 'components/mobile/screens/TeamDetailMobile';

/** `?tab=materials` swaps the pane, since Materials has no dedicated route; otherwise the student gradebook. */
const TeamDetailDesktop: React.FC = () => {
  const { t } = useTranslation();
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const tab = params.get('tab');

  const [assignOpen, setAssignOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const { team, isLoading, isError, refetch } = TeamsModule.Hooks.useSingle({
    id: teamId || '',
    enabled: Boolean(teamId),
  });
  const { collections, refetch: refetchCollections } = CollectionsModule.Hooks.useByTeam({
    teamId: teamId || '',
    enabled: Boolean(teamId),
  });

  const canInvite = usePermission(PERMISSIONS.TEAMS_INVITE);
  const canManage = usePermission(PERMISSIONS.TEAMS_EDIT);

  const isMaterials = tab === 'materials';

  const headerActions = isMaterials ? (
    canManage && (
      <Button size="sm" className="gap-1.5" onClick={() => setAssignOpen(true)}>
        <Plus className="h-4 w-4" />
        {t('group_share_material')}
      </Button>
    )
  ) : (
    <>
      {canInvite && (
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setInviteOpen(true)}>
          <Users className="h-4 w-4" />
          {t('group_invite_students')}
        </Button>
      )}
      {canManage && (
        <Button size="sm" className="gap-1.5" onClick={() => setAssignOpen(true)}>
          <Plus className="h-4 w-4" />
          {t('group_assign_collection')}
        </Button>
      )}
    </>
  );

  return (
    <div className="min-w-0">
      {isError ? (
        <EmptyState
          icon={<AlertCircle size={28} />}
          title={t('failed_to_load')}
          secondaryAction={{ label: t('retry'), onClick: () => refetch() }}
        />
      ) : isLoading ? (
        <GroupProfileSkeleton />
      ) : !team ? (
        <EmptyState
          icon={<GraduationCap size={28} />}
          title={t('group_detail_not_found')}
          secondaryAction={{ label: t('group_detail_back'), onClick: () => navigate('/app/teams') }}
        />
      ) : (
        <GroupProfile team={team} activeTab={isMaterials ? 'materials' : 'members'} actions={headerActions}>
          {isMaterials ? (
            <GroupMaterialsTab teamId={team.id} />
          ) : (
            <MembersGradebook team={team} onInvite={canInvite ? () => setInviteOpen(true) : undefined} />
          )}
        </GroupProfile>
      )}

      <AssignCollectionDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        teamId={teamId || ''}
        assignedCollectionIds={collections.map((c) => c.id)}
        onAssigned={() => refetchCollections()}
      />
      <InviteStudentsModal open={inviteOpen} onClose={() => setInviteOpen(false)} teamId={teamId || ''} />
    </div>
  );
};

// Phones render the dedicated mobile group detail (own tab strip); reads teamId
// from the route and hands it to the mobile screen.
export const TeamDetailPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  return useIsMobile() ? <TeamDetailMobile teamId={teamId || ''} /> : <TeamDetailDesktop />;
};

export default TeamDetailPage;
